import { useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useRef, useState } from 'react';
import { Dialog } from 'antd-mobile';
import { DspValidationType } from '../enums/DspValidationType';
import {
  VALIDATE_ORDER_EXISTS,
  ValidateOrderExistsResponse,
  ValidateOrderExistsVariables
} from '../pages/dsp/graphql/queries/validateOrder';
import {parseExceptionMessageKey} from "../utils/exceptionUtils";

export interface DspValidationOptions {
  /** 验证成功后的回调 */
  onSuccess?: (trackingNumber: string) => void;
  /** 验证失败后的回调 */
  onError?: (error: string, trackingNumber: string) => void;
  /** 是否显示默认的错误提示 */
  showDefaultError?: boolean;
  /** 用户确认后的回调（用于需要确认的错误场景） */
  onConfirm?: (trackingNumber: string, errorCode: string) => void;
  /** 用户取消后的回调（用于需要确认的错误场景） */
  onCancel?: (trackingNumber: string, errorCode: string) => void;
}

export interface DspValidationResult {
  /** 执行验证 */
  validatePackage: (trackingNumber: string, validationType?: DspValidationType) => void;
  /** 验证是否正在进行中 */
  loading: boolean;
  /** 获取错误消息的国际化文本 */
  getErrorMessage: (errorCode: string) => string;
  /** 弹框是否显示中（包括错误弹框和确认弹框） */
  isDialogVisible: boolean;
}

/**
 * DSP包裹验证Hook
 * 提供可复用的包裹验证功能，支持不同的验证场景和错误处理
 */
export const useDspValidation = (options: DspValidationOptions = {}): DspValidationResult => {
  const { t } = useTranslation();
  const {
    onSuccess,
    onError,
    showDefaultError = true,
    onConfirm,
    onCancel
  } = options;

  // 使用 useRef 来保存 trackingNumber，确保在回调中能正确访问
  const currentTrackingNumberRef = useRef<string>('');
  
  // 弹框显示状态
  const [isDialogVisible, setIsDialogVisible] = useState(false);

  const [validateOrder, { loading }] = useLazyQuery<ValidateOrderExistsResponse, ValidateOrderExistsVariables>(
    VALIDATE_ORDER_EXISTS,
    {
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        const isValid = data?.validateOrderExists;
        if (isValid && onSuccess) {
          onSuccess(currentTrackingNumberRef.current);
        }
      },
      onError: (error) => {
        console.error('Order validation error:', error);
        const errorMessageKey = parseExceptionMessageKey(error);
        const errorCode = extractErrorCode(errorMessageKey);
        const errorMessage = getErrorMessage(errorCode);
        
        // 检查是否是需要用户确认的错误类型
        if (isConfirmationRequiredError(errorCode)) {
          showConfirmationDialog(errorMessage, errorCode);
        } else if (showDefaultError) {
          showErrorDialog(errorMessage);
        }
        
        // 调用错误回调
        if (onError) {
          onError(errorCode, currentTrackingNumberRef.current);
        }
      }
    }
  );

  /**
   * 执行包裹验证
   */
  const validatePackage = (trackingNumber: string, validationType?: DspValidationType) => {
    currentTrackingNumberRef.current = trackingNumber;
    validateOrder({
      variables: {
        trackingNumber,
        validationType
      }
    });
  };

  /**
   * 从错误消息中提取错误代码
   */
  const extractErrorCode = (errorMessage: string): string => {
    const match = errorMessage.split('.').pop();
    if (match){
      return match;
    }
    return 'unknownError';
  };

  /**
   * 获取错误消息的国际化文本
   */
  const getErrorMessage = (errorCode: string): string => {
    const key = `pages.dsp.validation.errors.${errorCode}`;
    const message = t(key);
    
    // 如果翻译不存在，返回默认错误消息
    if (message === key) {
      return t('pages.dsp.validation.errors.unknownError');
    }
    
    return message;
  };

  /**
   * 判断是否是需要用户确认的错误类型
   */
  const isConfirmationRequiredError = (errorCode: string): boolean => {
    const confirmationRequiredErrors = [
      'modifyPod',           // ORDER_ALREADY_SIGNED_MODIFY_POD
      'notSignedYet'         // ORDER_NOT_SIGNED_YET
    ];
    return confirmationRequiredErrors.includes(errorCode);
  };

  /**
   * 显示确认对话框（带取消和确认按钮）
   */
  const showConfirmationDialog = (message: string, errorCode: string) => {
    setIsDialogVisible(true);
    Dialog.confirm({
      content: message,
      cancelText: t('pages.dsp.packageScan.cancel'),
      confirmText: t('pages.dsp.packageScan.confirm'),
      onCancel: () => {
        setIsDialogVisible(false);
        if (onCancel) {
          onCancel(currentTrackingNumberRef.current, errorCode);
        }
      },
      onConfirm: () => {
        setIsDialogVisible(false);
        if (onConfirm) {
          onConfirm(currentTrackingNumberRef.current, errorCode);
        }
      }
    });
  };

  /**
   * 显示错误对话框（仅确认按钮）
   */
  const showErrorDialog = (message: string) => {
    setIsDialogVisible(true);
    Dialog.alert({
      content: message,
      confirmText: t('pages.dsp.packageScan.confirm'),
      onConfirm: () => {
        setIsDialogVisible(false);
        // 可以在这里添加额外的确认逻辑
      }
    });
  };

  return {
    validatePackage,
    loading,
    getErrorMessage,
    isDialogVisible
  };
};
